// Phase 2 settings shared by the /try client (src/) and alma-conversation-public.
// Keep this file import-free so both Vite and Deno can load it as-is.

export type FollowUpMode = 'always' | 'if_vague' | 'never';

/** One of Alma's main Part 2 questions, as an admin writes it. */
export interface Part2Question {
  question: string;
  follow_up: FollowUpMode;
  /** What a follow-up should go after (only used when follow_up is 'always'). */
  follow_up_hint?: string | null;
}

/** Everything about Part 2 an admin can change. Lives in the database; the values below are only the fallback. */
export interface Part2Config {
  /** Active questions, in order. Their count is the progress bar's total. */
  questions: Part2Question[];
  /** The instructions around the questions. `{budget}`, `{questions}` and `{marker}` are filled in. */
  rules: string;
  /** How Alma follows up a question set to "only when the answer is vague". */
  followUpIfVague: string;
  /**
   * Traveler replies past the question count before the server tells Alma to
   * close. Null: never force a close, Alma wraps up when she has what she needs.
   */
  closeAfterExtraTurns: number | null;
  /** What the portrait model is told for each of these fields. */
  portrait: { archetype: string; summary: string; tripName: string };
}

export const DEFAULT_PART2_RULES = `## Part 2 — who they are, not logistics
The traveler has just answered a few quick questions; their answers are under "What you already know". The conversation begins with a note saying so — the traveler didn't type it. Now you have a short conversation to understand who they are, so the host fits them as a person, not just their list of interests.
In Part 2, these rules take precedence over anything above about how many exchanges to have, what to learn, or inviting them to recall past moments.
- Never re-ask anything in "What you already know". Don't ask for cities, dates, number of days, who's coming, what matters most, or their name. You may refer to them ("With four of you and food first…").
- Be direct and keep your class: one short bridge clause at most, then the question. No preamble, no explaining why you ask.
- Ask these {budget} questions, in this order. Adapt the wording lightly so it flows from what they just said, but keep each one's meaning:
{questions}
- Keep every question and follow-up about them as a person. Don't ask them to walk you through a day, to describe their last trip, to recall a past trip they loved, or to take you to a specific moment, place or scene.
- Write your reaction to what they said and your next question as two short paragraphs, with a blank line between them: the traveler sees them as two messages. Keep both within the length limit.
- If "What you already know" says someone has limited mobility, ask first, gently and in one sentence, what the host should know to plan around it. It isn't one of your {budget} questions. Then go on with the first question.
- Your first message opens with the bridge from the practical side to who they are (see Transitions), then asks your first question.
- After their answer to the last question, close, following the closing rules below.`;

export const DEFAULT_FOLLOW_UP_IF_VAGUE = 'Follow up at most once, only when the answer is a word or two or generic. Ask what they love about it or why it matters to them: about them, never for a specific moment, place or scene from a past trip.';

export const DEFAULT_PART2_QUESTIONS: Part2Question[] = [
  {
    question: 'Something about them a good host should know: a habit, an obsession, a small quirk that shapes how they travel.',
    follow_up: 'always',
    follow_up_hint: "go after what's behind their answer (what it gives them, why it matters to them, or when it started), to understand their personality rather than just the fact.",
  },
  { question: "What's something they could happily talk about for an hour?", follow_up: 'if_vague', follow_up_hint: null },
  { question: "What went badly on a past trip that they don't want repeated.", follow_up: 'if_vague', follow_up_hint: null },
  { question: `"When you're back home and someone asks about this trip, what would you like to tell them?"`, follow_up: 'never', follow_up_hint: null },
  { question: "Ask whether there's anything more they'd like you to know about them before you match their taste with a host's.", follow_up: 'never', follow_up_hint: null },
];

export const DEFAULT_PORTRAIT_ARCHETYPE = 'A 2-4 word name for this traveler\'s travel personality, in title case, starting with "The" (e.g. "The Unhurried Noticer", "The Curious Regular", "The Table Gatherer"). Read it from their temperament and how they move through a place, never from a single place, food or activity they named, and never a destination.';

export const DEFAULT_PORTRAIT_SUMMARY = 'Alma\'s read of who this traveler is: 2-3 sentences written TO them (address them as "you"), in Alma\'s voice. Interpret, never recap: say what their answers reveal about their temperament, what they value and how they like to move through a place, the pattern behind what they said, the way a perceptive friend would put it. Never quote or paraphrase their answers, never list the places, foods or activities they named, and never mention a past trip: those details belong in style, interests and keywords, not here. It should make them think "how did she know?". e.g. "You travel to feel at home somewhere new, not to collect sights. You notice the small rituals of a place, and you would rather go deep on a few things than skim many." Never generic filler ("You enjoy exploring new places").';

export const DEFAULT_PORTRAIT_TRIP_NAME = 'A short, evocative name for THIS specific trip (3-6 words). Draw it from the traveler\'s personality and how they like to travel overall (the same read as archetype and raw_summary): the mood, the pace, what they value. Never from a single story, a past trip, or a detail they mentioned once, so it never reads as if Alma misunderstood the trip. NEVER include a city, country, or any destination name in this name, under any circumstance — the destination is already shown separately everywhere this renders, so naming it here is always redundant. e.g. "Late Nights and Long Tables", "Slow Mornings, No Plans", "Tascas and Long Dinners". Never generic ("My Trip", "Portugal Trip") and never destination-based ("Lisbon Late Nights", "Slow Mornings in Porto").';

export const DEFAULT_PART2: Part2Config = {
  questions: DEFAULT_PART2_QUESTIONS,
  rules: DEFAULT_PART2_RULES,
  followUpIfVague: DEFAULT_FOLLOW_UP_IF_VAGUE,
  closeAfterExtraTurns: null,
  portrait: { archetype: DEFAULT_PORTRAIT_ARCHETYPE, summary: DEFAULT_PORTRAIT_SUMMARY, tripName: DEFAULT_PORTRAIT_TRIP_NAME },
};

/** Keys of alma_part2_settings. */
export const PART2_SETTING_KEYS = {
  rules: 'part2_rules',
  closeAfter: 'part2_close_after_extra_turns',
  followUpIfVague: 'part2_follow_up_if_vague',
  archetype: 'portrait_archetype',
  summary: 'portrait_summary',
  tripName: 'portrait_trip_name',
} as const;

const MODES: FollowUpMode[] = ['always', 'if_vague', 'never'];

/**
 * The config from the database rows, using the default for anything missing
 * or blank — a failed or empty lookup must never leave Alma without questions.
 */
export function part2ConfigFromRows(
  questionRows: { question?: unknown; follow_up?: unknown; follow_up_hint?: unknown }[] | null | undefined,
  settingRows: { key?: unknown; value?: unknown }[] | null | undefined,
): Part2Config {
  const settings = new Map((settingRows ?? []).map((r) => [String(r.key), typeof r.value === 'string' ? r.value : '']));
  const text = (key: string, fallback: string) => (settings.get(key)?.trim() ? (settings.get(key) as string) : fallback);

  const questions = (questionRows ?? []).flatMap((r): Part2Question[] => {
    const question = typeof r.question === 'string' ? r.question.trim() : '';
    if (!question) return [];
    const follow_up = MODES.find((m) => m === r.follow_up) ?? 'if_vague';
    const hint = typeof r.follow_up_hint === 'string' && r.follow_up_hint.trim() ? r.follow_up_hint.trim() : null;
    return [{ question, follow_up, follow_up_hint: hint }];
  });

  const closeRaw = settings.get(PART2_SETTING_KEYS.closeAfter)?.trim() ?? '';
  const closeN = closeRaw === '' ? null : Number.parseInt(closeRaw, 10);

  return {
    questions: questions.length ? questions : DEFAULT_PART2.questions,
    rules: text(PART2_SETTING_KEYS.rules, DEFAULT_PART2.rules),
    followUpIfVague: text(PART2_SETTING_KEYS.followUpIfVague, DEFAULT_FOLLOW_UP_IF_VAGUE),
    closeAfterExtraTurns: closeN !== null && Number.isFinite(closeN) && closeN >= 0 ? closeN : null,
    portrait: {
      archetype: text(PART2_SETTING_KEYS.archetype, DEFAULT_PART2.portrait.archetype),
      summary: text(PART2_SETTING_KEYS.summary, DEFAULT_PART2.portrait.summary),
      tripName: text(PART2_SETTING_KEYS.tripName, DEFAULT_PART2.portrait.tripName),
    },
  };
}

/** Alma ends each main question with this (stripped by the client) so the progress bar can count them. */
export const ALMA_QUESTION_MARKER = '<<<Q>>>';

/**
 * The hidden first traveler turn of Part 2. The model only sees Part 2, which
 * begins with Alma speaking, and the Messages API needs a user turn first —
 * without this, Alma's first question would be dropped from her own context.
 */
export const ALMA_P2_KICKOFF = '[The traveler has finished the quick questions. Start Part 2.]';
